import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { MoreVertical } from 'lucide-react'

/**
 * Row Actions kebab menu — the standard Actions-column control for the
 * DataTable. A single "⋮" button opens a compact dropdown of actions,
 * mirroring the table standard (View / Edit / Copy … / Delete-in-red).
 *
 * Replaces per-row clusters of inline icon buttons: one affordance,
 * labelled actions, destructive items tinted red and pushed to the
 * bottom behind a divider.
 *
 * The menu renders through a portal and is positioned next to the
 * trigger (flips above when there isn't room below, right-aligned so it
 * never runs off-screen). Closes on outside click, Escape, or scroll.
 *
 * @param {Array<{key,label,icon?,onClick,danger?,disabled?,separatorBefore?}|false|null>} items
 *   Falsy entries are ignored, so callers can inline conditionals:
 *   `isActive && { key:'suspend', ... }`.
 * @param {string} [label='Actions'] Accessible label for the trigger.
 */
export function RowActionsMenu({ items, label = 'Actions' }) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState(null)
  const btnRef = useRef(null)
  const menuRef = useRef(null)

  const visible = (items || []).filter(Boolean)

  function place() {
    const btn = btnRef.current
    if (!btn) return
    const r = btn.getBoundingClientRect()
    const width = 208
    const estH = visible.length * 38 + 12
    const spaceBelow = window.innerHeight - r.bottom
    const top = spaceBelow < estH + 12 ? r.top - estH - 8 : r.bottom + 8
    setPos({
      top: Math.max(8, top),
      left: Math.max(8, Math.min(r.right - width, window.innerWidth - width - 8)),
      width,
    })
  }

  useLayoutEffect(() => {
    if (open) place()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    if (!open) return
    const close = () => setOpen(false)
    const onDown = (e) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        btnRef.current &&
        !btnRef.current.contains(e.target)
      )
        setOpen(false)
    }
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false)
        btnRef.current?.focus()
      }
    }
    const t = setTimeout(() => document.addEventListener('mousedown', onDown), 0)
    document.addEventListener('keydown', onKey)
    // Scroll can move the trigger out from under the (fixed) menu, so
    // close rather than leave a detached popover.
    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)
    return () => {
      clearTimeout(t)
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close)
    }
  }, [open])

  if (!visible.length) return <span aria-hidden="true" />

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        title={label}
        className={
          'inline-flex h-8 w-8 items-center justify-center rounded-md cursor-pointer transition-colors ' +
          (open
            ? 'bg-[color:var(--color-card-hover)] text-[color:var(--color-ink-strong)]'
            : 'text-[color:var(--color-ink-muted)] hover:bg-[color:var(--color-card-hover)] hover:text-[color:var(--color-ink-strong)]')
        }
      >
        <MoreVertical size={18} />
      </button>

      {open && pos
        ? createPortal(
            <div
              ref={menuRef}
              role="menu"
              aria-label={label}
              className="fixed z-[72] rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-xl py-1"
              style={{ top: pos.top, left: pos.left, width: pos.width }}
            >
              {visible.map((it) => (
                <div key={it.key}>
                  {it.separatorBefore ? (
                    <hr className="my-1 border-0 border-t border-[color:var(--color-rule)]" />
                  ) : null}
                  <button
                    type="button"
                    role="menuitem"
                    disabled={it.disabled}
                    onClick={() => {
                      setOpen(false)
                      it.onClick?.()
                    }}
                    className={
                      'flex w-full items-center gap-2.5 px-3 py-2 text-sm cursor-pointer transition-colors disabled:opacity-40 disabled:cursor-not-allowed ' +
                      (it.danger
                        ? 'text-[color:var(--color-priority-urgent)] hover:bg-[color:var(--color-priority-urgent)]/10'
                        : 'text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)]')
                    }
                  >
                    {it.icon ? <it.icon size={15} strokeWidth={1.75} /> : null}
                    {it.label}
                  </button>
                </div>
              ))}
            </div>,
            document.body,
          )
        : null}
    </>
  )
}
